/**
 * Board Files API service - handles board file attachment API calls
 */

import { apiClient } from './client';
import type { Attachment, AttachmentUploadRequest, AttachmentUploadResponse } from './comments';
import { useAuthStore } from '@stores/authStore';

// Re-export types from comments for convenience
export type { Attachment, AttachmentUploadRequest, AttachmentUploadResponse };

// =============================================================================
// Board Files API
// =============================================================================

export const boardFilesApi = {
  /**
   * Get all files attached to a board
   */
  list: async (boardId: string): Promise<Attachment[]> => {
    const response = await apiClient.get<unknown[]>(`/boards/${boardId}/files`);
    return response.data.map(transformAttachment);
  },

  /**
   * Initiate a file upload for a board
   */
  initiateUpload: async (
    boardId: string,
    data: AttachmentUploadRequest
  ): Promise<AttachmentUploadResponse> => {
    const response = await apiClient.post<unknown>(`/boards/${boardId}/files`, {
      filename: data.filename,
      content_type: data.contentType,
      size_bytes: data.sizeBytes,
    });
    return transformAttachmentUploadResponse(response.data);
  },

  /**
   * Upload file data to the upload URL
   */
  uploadFile: async (uploadUrl: string, file: File): Promise<void> => {
    const { accessToken } = useAuthStore.getState();
    const headers: HeadersInit = {
      'Content-Type': file.type || 'application/octet-stream',
    };
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers,
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
    }
  },

  /**
   * Confirm that an upload is complete
   */
  confirmUpload: async (attachmentId: string, fileHash?: string): Promise<Attachment> => {
    const response = await apiClient.post<unknown>(`/attachments/${attachmentId}/confirm`, {
      file_hash: fileHash,
    });
    return transformAttachment(response.data);
  },

  /**
   * Get attachment details (refreshes download URL)
   */
  getAttachment: async (attachmentId: string): Promise<Attachment> => {
    const response = await apiClient.get<unknown>(`/attachments/${attachmentId}`);
    return transformAttachment(response.data);
  },

  /**
   * Delete a board file
   */
  delete: async (attachmentId: string): Promise<void> => {
    await apiClient.delete(`/attachments/${attachmentId}`);
  },
};

// =============================================================================
// Transform helpers
// =============================================================================

/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
function transformAttachment(data: any): Attachment {
  const attachment: Attachment = {
    id: data.id,
    filename: data.filename,
    contentType: data.contentType ?? data.content_type,
    attachmentType: data.attachmentType ?? data.attachment_type,
    sizeBytes: data.sizeBytes ?? data.size_bytes,
    status: data.status,
    uploadedById: data.uploadedById ?? data.uploaded_by_id,
    createdAt: data.createdAt ?? data.created_at,
    updatedAt: data.updatedAt ?? data.updated_at,
  };
  if (data.url) attachment.url = data.url;
  if (data.thumbnailUrl ?? data.thumbnail_url)
    attachment.thumbnailUrl = data.thumbnailUrl ?? data.thumbnail_url;
  return attachment;
}

function transformAttachmentUploadResponse(data: any): AttachmentUploadResponse {
  return {
    attachmentId: data.attachmentId ?? data.attachment_id,
    uploadUrl: data.uploadUrl ?? data.upload_url,
    expiresAt: data.expiresAt ?? data.expires_at,
  };
}
/* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */

export default boardFilesApi;
